/*
A CVSS v3 and v31 score calculator.
Based on:
- https://www.first.org/cvss/v3.0/user-guide
- https://www.first.org/cvss/calculator/3.1
- https://nvd.nist.gov/vuln-metrics/cvss/v3-calculator
Emits:
 @update
Pass in individual metric (one of):
AV, AC, PR, UI, S, C, I, A, E, RL, RC, CR, IR, AR, MAV, MAC, MPR, MUI, MS, MC, MI, MA
or pass in the whole CVSS vector.
*/
import VueApexCharts from 'vue-apexcharts';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import { CVSSv3 } from '../../../assets/libs/3rd-party/cvss3';
import { CVSS31 } from '../../../assets/libs/3rd-party/cvss31';

const CVSS_V31_VERSION_STRING = '3.1';

const RADAR_V3_LABELS = [
    'Attack Surface Exposure',
    'Ease of Exploit (Technical)',
    'Ease of Exploit (Privileges)',
    'Ease of Exploit (User Interaction)',
    'Impact (Attack Chain)',
    'Risk to Data Confidentiality',
    'Risk to Data Integrity',
    'Risk to System Availability',
    'Risk of Exploit (Exploit Maturity)',
    'Difficulty of Remediation',
    'Confidence in Vulnerability Report',
];

// const RADAR_V3_CATEGORIES = [
//   'Attack Vector',
//   'Attack Complexity',
//   'Privileges Required',
//   'User Interaction',
//   'Scope',
//   'Confidentiality Impact',
//   'Integrity Impact',
//   'Availability Impact',
//   'Exploit Code Maturity',
//   'Remediation Level',
//   'Report Confidence',
// ];

const RADIAL_V3_MAPPING = {
    AV: {
        N: 5,
        A: 4,
        L: 3,
        P: 2,
    },
    AC: {
        L: 4,
        H: 2,
    },
    PR: {
        N: 5,
        L: 3,
        H: 2,
    },
    UI: {
        N: 4,
        R: 2,
    },
    S: {
        C: 4,
        U: 2,
    },
    C: {
        H: {
            H: 5,
            M: 4,
            L: 3,
            X: 4,
        },
        L: {
            H: 3,
            M: 2,
            L: 1,
            X: 2,
        },
        N: {
            H: 0,
            M: 0,
            L: 0,
            X: 0,
        },
        X: {
            H: 4,
            M: 4,
            L: 3,
            X: 2,
        },
    },
    I: {
        H: {
            H: 5,
            M: 4,
            L: 3,
            X: 4,
        },
        L: {
            H: 3,
            M: 2,
            L: 1,
            X: 2,
        },
        N: {
            H: 0,
            M: 0,
            L: 0,
            X: 0,
        },
        X: {
            H: 4,
            M: 4,
            L: 3,
            X: 2,
        },
    },
    A: {
        H: {
            H: 5,
            M: 4,
            L: 3,
            X: 4,
        },
        L: {
            H: 3,
            M: 2,
            L: 1,
            X: 2,
        },
        N: {
            H: 0,
            M: 0,
            L: 0,
            X: 0,
        },
        X: {
            H: 4,
            M: 4,
            L: 3,
            X: 2,
        },
    },
    E: {
        H: 5,
        F: 4,
        P: 3,
        U: 2,
        X: 3,
    },
    RL: {
        U: 5,
        W: 4,
        T: 3,
        O: 2,
        X: 3,
    },
    RC: {
        C: 5,
        R: 3,
        U: 2,
        X: 3,
    },
};

const RADAR_V3_LABEL_NAMES = {
    5: 'Very High',
    4: 'High',
    3: 'Medium',
    2: 'Low',
    1: 'Very Low',
    0: 'None',
};

export const CVSSV3Calculator = {
    props: {
        cvssVector: {
            type: String,
            default: null,
        },
        cvssAV: {
            type: String,
            default: null,
        },
        cvssAC: {
            type: String,
            default: null,
        },
        cvssPR: {
            type: String,
            default: null,
        },
        cvssUI: {
            type: String,
            default: null,
        },
        cvssS: {
            type: String,
            default: null,
        },
        cvssC: {
            type: String,
            default: null,
        },
        cvssI: {
            type: String,
            default: null,
        },
        cvssA: {
            type: String,
            default: null,
        },
        cvssE: {
            type: String,
            default: 'X',
        },
        cvssRL: {
            type: String,
            default: 'X',
        },
        cvssRC: {
            type: String,
            default: 'X',
        },
        cvssCR: {
            type: String,
            default: 'X',
        },
        cvssIR: {
            type: String,
            default: 'X',
        },
        cvssAR: {
            type: String,
            default: 'X',
        },
        cvssMAV: {
            type: String,
            default: 'X',
        },
        cvssMAC: {
            type: String,
            default: 'X',
        },
        cvssMPR: {
            type: String,
            default: 'X',
        },
        cvssMUI: {
            type: String,
            default: 'X',
        },
        cvssMS: {
            type: String,
            default: 'X',
        },
        cvssMC: {
            type: String,
            default: 'X',
        },
        cvssMI: {
            type: String,
            default: 'X',
        },
        cvssMA: {
            type: String,
            default: 'X',
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        apexchart: VueApexCharts,
    },
    data() {
        const chartBarSeries = [
            {
                name: 'CVSS V3',
                data: [0, 0, 0],
            },
        ];
        const chartBarHeight = 125;
        const chartBarOptions = {
            chart: {
                id: 'cvss-v3-score-chart',
                height: chartBarHeight,
                type: 'bar',
            },
            stroke: {
                width: 2,
            },
            fill: {
                opacity: 0.8,
                colors: ['#428bca'],
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    horizontal: true,
                },
            },
            dataLabels: {
                enabled: false,
            },
            yaxis: {
                min: 0,
                max: 10,
                tickAmount: 10,
            },
            xaxis: {
                categories: ['Base Score', 'Temporal Score', 'Environmental Score'],
            },
            series: [
                {
                    name: 'Scores',
                    data: [0, 0, 0],
                },
            ],
        };
        const chartType = 'radar';
        const chartHeight = 250;
        const chartSeries = [
            {
                name: 'CVSS V3',
                data: RADAR_V3_LABELS.map(() => 0),
            },
        ];
        const chartOptions = {
            chart: {
                id: 'cvss-v3-score-chart',
                height: chartHeight,
                type: chartType,
            },
            stroke: {
                width: 2,
            },
            theme: {
                palette: 'palette2',
            },
            plotOptions: {
                radar: {
                    size: 100,
                    polygons: {
                        strokeColors: '#e9e9e9',
                        fill: {
                            colors: ['#f8f8f8', '#fff'],
                        },
                    },
                },
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                y: {
                    formatter: (e) => RADAR_V3_LABEL_NAMES[e],
                },
            },
            colors: ['#FF4560'],
            markers: {
                size: 4,
                colors: ['#fff'],
                strokeColor: '#FF4560',
                strokeWidth: 2,
            },
            yaxis: {
                min: 0,
                max: 5,
                tickAmount: 5,
                labels: {
                    formatter: () => '', // RADAR_V3_LABEL_NAMES[e],
                },
            },
            xaxis: {
                categories: RADAR_V3_LABELS,
            },
            series: chartSeries,
        };
        return {
            badgeClass: 'badge badge-cvss',
            score: null,
            baseScore: null,
            temporalScore: null,
            environmentalScore: null,
            vector: this.cvssVector ?? '-',
            AV: this.cvssAV,
            AC: this.cvssAC,
            PR: this.cvssPR,
            UI: this.cvssUI,
            S: this.cvssS,
            C: this.cvssC,
            I: this.cvssI,
            A: this.cvssA,
            E: this.cvssE,
            RL: this.cvssRL,
            RC: this.cvssRC,
            CR: this.cvssCR,
            IR: this.cvssIR,
            AR: this.cvssAR,
            MAV: this.cvssMAV,
            MAC: this.cvssMAC,
            MPR: this.cvssMPR,
            MUI: this.cvssMUI,
            MS: this.cvssMS,
            MC: this.cvssMC,
            MI: this.cvssMI,
            MA: this.cvssMA,
            chartOptions,
            chartSeries,
            chartType,
            chartHeight,
            chartBarSeries,
            chartBarHeight,
            chartBarOptions,
            calculator: CVSSv3,
        };
    },
    computed: {},
    methods: {
        CVSSColour(score) {
            if (score === 0) {
                return 'cvss-zero';
            }
            if (score < 4.0) {
                return 'cvss-low';
            }
            if (score < 7.0) {
                return 'cvss-medium';
            }
            if (score < 9.0) {
                return 'cvss-high';
            }
            return 'cvss-critical';
        },
        calculateRadarChartScores() {
            return Object.keys(RADIAL_V3_MAPPING).map((e) => {
                if (e === 'C' || e === 'I' || e === 'A') {
                    const k = `${e}R`;
                    return RADIAL_V3_MAPPING[e][this[e]][this[k]];
                }
                return RADIAL_V3_MAPPING[e][this[e]];
            });
        },
        checkBase() {
            // returns whether all base values are selected
            return [
                this.AV,
                this.AC,
                this.PR,
                this.UI,
                this.S,
                this.C,
                this.I,
                this.A,
            ].some((e) => !e);
        },
        updateChartBarSeries(series) {
            this.chartBarSeries = series;
            this.chartBarOptions = {
                ...this.chartBarOptions,
                series,
            };
        },
        updateChartSeries(series) {
            this.chartSeries = series;
            this.chartOptions = {
                ...this.chartOptions,
                series,
            };
        },
        updateMetrics(
            AV,
            AC,
            PR,
            UI,
            S,
            C,
            I,
            A,
            E,
            RL,
            RC,
            CR,
            IR,
            AR,
            MAV,
            MAC,
            MPR,
            MUI,
            MS,
            MC,
            MI,
            MA,
        ) {
            if (AV) this.AV = AV;
            if (AC) this.AC = AC;
            if (PR) this.PR = PR;
            if (UI) this.UI = UI;
            if (S) this.S = S;
            if (C) this.C = C;
            if (I) this.I = I;
            if (A) this.A = A;
            if (E) this.E = E;
            if (RL) this.RL = RL;
            if (RC) this.RC = RC;
            if (CR) this.CR = CR;
            if (IR) this.IR = IR;
            if (AR) this.AR = AR;
            if (MAV) this.MAV = MAV;
            if (MAC) this.MAC = MAC;
            if (MPR) this.MPR = MPR;
            if (MUI) this.MUI = MUI;
            if (MS) this.MS = MS;
            if (MC) this.MC = MC;
            if (MI) this.MI = MI;
            if (MA) this.MA = MA;
        },
        updateScoreVector(
            baseMetricScore,
            temporalMetricScore,
            environmentalMetricScore,
            vectorString,
        ) {
            this.vector = vectorString;
            this.baseScore = baseMetricScore;
            this.temporalScore = temporalMetricScore;
            this.environmentalScore = environmentalMetricScore;
            this.updateChartBarSeries([
                {
                    name: 'CVSS V3',
                    data: [this.baseScore, this.temporalScore, this.environmentalScore],
                },
            ]);
            this.updateChartSeries([
                {
                    name: 'CVSS V3',
                    data: this.calculateRadarChartScores(),
                },
            ]);
            this.$emit('update', {
                vector: this.vector,
                baseScore: this.baseScore,
                temporalScore: this.temporalScore,
                environmentalScore: this.environmentalScore,
            });
        },
        calculateScore() {
            try {
                if (this.checkBase()) {
                    // some of the base selects are missing - no update possible
                    return;
                }
                const {
                    success,
                    vectorString,
                    baseMetricScore,
                    temporalMetricScore,
                    environmentalMetricScore,
                } = this.calculator.calculateCVSSFromMetrics(
                    this.AV,
                    this.AC,
                    this.PR,
                    this.UI,
                    this.S,
                    this.C,
                    this.I,
                    this.A,
                    this.E,
                    this.RL,
                    this.RC,
                    this.CR,
                    this.IR,
                    this.AR,
                    this.MAV,
                    this.MAC,
                    this.MPR,
                    this.MUI,
                    this.MS,
                    this.MC,
                    this.MI,
                    this.MA,
                );
                if (success) {
                    this.updateScoreVector(
                        baseMetricScore,
                        temporalMetricScore,
                        environmentalMetricScore,
                        vectorString,
                    );
                }
            } catch (e) {
                //
            }
        },
        updateScoreFromVector(vector) {
            try {
                const {
                    success,
                    AV,
                    AC,
                    PR,
                    UI,
                    S,
                    C,
                    I,
                    A,
                    E,
                    RL,
                    RC,
                    CR,
                    IR,
                    AR,
                    MAV,
                    MAC,
                    MPR,
                    MUI,
                    MS,
                    MC,
                    MI,
                    MA,
                    vectorString,
                    baseMetricScore,
                    temporalMetricScore,
                    environmentalMetricScore,
                } = this.calculator.calculateCVSSFromVector(vector);
                if (success) {
                    this.updateMetrics(
                        AV,
                        AC,
                        PR,
                        UI,
                        S,
                        C,
                        I,
                        A,
                        E,
                        RL,
                        RC,
                        CR,
                        IR,
                        AR,
                        MAV,
                        MAC,
                        MPR,
                        MUI,
                        MS,
                        MC,
                        MI,
                        MA,
                    );
                    this.updateScoreVector(
                        baseMetricScore,
                        temporalMetricScore,
                        environmentalMetricScore,
                        vectorString,
                    );
                }
            } catch (e) {
                //
            }
        },
        handleVectorChange: debounce(function handler(event) {
            try {
                const vector = event?.target?.value;
                this.calculator = vector.includes(CVSS_V31_VERSION_STRING)
                    ? CVSS31
                    : CVSSv3;
                if (vector) {
                    this.updateScoreFromVector(vector);
                }
            } catch (e) {
                //
            }
        }, 50),
    },
    watch: {},
    beforeDestroy() {
        if (this.$refs['cvss-v3-chart-1'] && this.$refs['cvss-v3-chart-1'].chart) {
            this.$refs['cvss-v3-chart-1'].chart.destroy();
        }
        if (this.$refs['cvss-v3-chart-2'] && this.$refs['cvss-v3-chart-2'].chart) {
            this.$refs['cvss-v3-chart-2'].chart.destroy();
        }
    },
    mounted() {
        if (this.vector) {
            this.calculator = this.vector.includes(CVSS_V31_VERSION_STRING)
                ? CVSS31
                : CVSSv3;
            this.updateScoreFromVector(this.vector);
        }
    },
    template: `
  <!--

  Copyright (c) 2019, FIRST.ORG, INC.
  All rights reserved.

  Redistribution and use in source and binary forms, with or without modification, are permitted provided that the
  following conditions are met:
  1. Redistributions of source code must retain the above copyright notice, this list of conditions and the following
     disclaimer.
  2. Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the
     following disclaimer in the documentation and/or other materials provided with the distribution.
  3. Neither the name of the copyright holder nor the names of its contributors may be used to endorse or promote
     products derived from this software without specific prior written permission.

  THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES,
  INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
  DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
  SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
  SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
  WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
  OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
-->
    <div class="cvss-calculator">

    <div v-bind:style="{ height: chartHeight*1.05, paddingBottom: '0.25rem' }">
        <apexchart ref="cvss-v3-chart-2" :type="chartType" :options="chartOptions" :series="chartSeries"
            :height="chartHeight" />
    </div>

    <div v-bind:style="{ height: chartBarHeight*1.05, paddingBottom: '0.25rem' }">
        <apexchart ref="cvss-v3-chart-1" type="bar" :options="chartBarOptions" :series="chartBarSeries"
            :height="chartBarHeight" />
    </div>

        <div class="cvss-score-rating">
            <p v-if="!baseScore" class="alert alert-dark p-2 m-2">
                Select values for all base metrics to generate score
            </p>
            <span id="CVSS_v3_cvss-base-metric-core"></span>
            <span id="CVSS_v3_cvss-base-severity"></span>
        </div>

        <dl class="cvss-fieldset cvss-score-rating">
            <dt class="col-sm-3">Base Score: </dt>
            <dd class="col-sm-9">
                <span v-bind:class="[badgeClass, CVSSColour(baseScore)]">{{ baseScore ?? "-" }}</span>
            </dd>

            <dt class="col-sm-3">Temporal Score: </dt>
            <dd class="col-sm-9">
                <span v-bind:class="[badgeClass, CVSSColour(temporalScore)]">{{ temporalScore ?? "-" }}</span>
            </dd>

            <dt class="col-sm-3">Environmental Score: </dt>
            <dd class="col-sm-9">
                <span v-bind:class="[badgeClass, CVSSColour(environmentalScore)]">{{ environmentalScore ?? "-" }}</span>
            </dd>

            <dt class="col-sm-3">Vector: </dt>
            <dd class="col-sm-9">
                <input :disabled="true" class="form-control w-100" type="text" v-model="vector" @change="handleVectorChange" />
            </dd>
        </dl>


        <fieldset id="CVSS_v3_baseMetricGroup" class="cvss-fieldset">
            <legend class="font-weight-bold" id="CVSS_v3_baseMetricGroup_Legend"
                title="The Base Metric group represents the intrinsic  characteristics of a vulnerability that are constant over time and across user environments. Determine the vulnerable component and score Attack Vector, Attack Complexity, Privileges Required and User Interaction relative to this.">
                Base Score</legend>

            <div class="col-lg-6">

                <div class="cvss-metric">
                    <div id="CVSS_v3_AV_Heading"
                        title="This metric reflects the context by which vulnerability exploitation is possible. The Base Score increases the more remote (logically, and physically) an attacker can be in order to exploit the vulnerable component.">
                        Attack Vector (AV)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V3_AV" value="N" id="CVSS_v3_AV_N" type="radio" /><label
                        for="CVSS_v3_AV_N" id="CVSS_v3_AV_N_Label"
                        title="The vulnerable component is bound to the network stack and the set of possible attackers extends beyond the other options listed, up to and including the entire Internet. Such a vulnerability is often termed 'remotely exploitable' and can be thought of as an attack being exploitable at the protocol level one or more network hops away (e.g., across one or more routers).">Network
                        (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V3_AV" value="A" id="CVSS_v3_AV_A" type="radio" /><label
                        for="CVSS_v3_AV_A" id="CVSS_v3_AV_A_Label"
                        title="The vulnerable component is bound to the network stack, but the attack is limited at the protocol level to a logically adjacent topology. This can mean an attack must be launched from the same shared physical (e.g., Bluetooth or IEEE 802.11) or logical (e.g., local IP subnet) network, or from within a secure or otherwise limited administrative domain (e.g., MPLS, secure VPN to an administrative network zone).">Adjacent
                        (A)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V3_AV" value="L" id="CVSS_v3_AV_L" type="radio" /><label
                        for="CVSS_v3_AV_L" id="CVSS_v3_AV_L_Label"
                        title="The vulnerable component is not bound to the network stack and the attacker’s path is via read/write/execute capabilities. Either: the attacker exploits the vulnerability by accessing the target system locally (e.g., keyboard, console), or remotely (e.g., SSH); or the attacker relies on User Interaction by another person to perform actions required to exploit the vulnerability (e.g., tricking a legitimate user into opening a malicious document).">Local
                        (L)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AV" name="CVSS_V3_AV" value="P" id="CVSS_v3_AV_P" type="radio" /><label
                        for="CVSS_v3_AV_P" id="CVSS_v3_AV_P_Label"
                        title="The attack requires the attacker to physically touch or manipulate the vulnerable component. Physical interaction may be brief or persistent.">Physical
                        (P)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_AC_Heading"
                        title="This metric describes the conditions beyond the attacker’s control that must exist in order to exploit the vulnerability. Such conditions may require the collection of more information about the target or computational exceptions. The assessment of this metric excludes any requirements for user interaction in order to exploit the vulnerability. If a specific configuration is required for an attack to succeed, the Base metrics should be scored assuming the vulnerable component is in that configuration.">
                        Attack Complexity (AC)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AC" name="CVSS_V3_AC" value="L" id="CVSS_v3_AC_L" type="radio" /><label
                        for="CVSS_v3_AC_L" id="CVSS_v3_AC_L_Label"
                        title="Specialized access conditions or extenuating circumstances do not exist. An attacker can expect repeatable success against the vulnerable component.">Low
                        (L)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="AC" name="CVSS_V3_AC" value="H" id="CVSS_v3_AC_H" type="radio" /><label
                        for="CVSS_v3_AC_H" id="CVSS_v3_AC_H_Label"
                        title="A successful attack depends on conditions beyond the attacker's control. That is, a successful attack cannot be accomplished at will, but requires the attacker to invest in some measurable amount of effort in preparation or execution against the vulnerable component before a successful attack can be expected. For example, a successful attack may require an attacker to: gather knowledge about the environment in which the vulnerable target/component exists; prepare the target environment to improve exploit reliability; or inject themselves into the logical network path between the target and the resource requested by the victim in order to read and/or modify network communications (e.g., a man in the middle attack).">High
                        (H)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_PR_Heading"
                        title="This metric describes the level of privileges an attacker must possess before successfully exploiting the vulnerability.">
                        Privileges Required (PR)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="PR" name="CVSS_V3_PR" value="N" id="CVSS_v3_PR_N" type="radio" /><label
                        for="CVSS_v3_PR_N" id="CVSS_v3_PR_N_Label"
                        title="The attacker is unauthorized prior to attack, and therefore does not require any access to settings or files to carry out an attack.">None
                        (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="PR" name="CVSS_V3_PR" value="L" id="CVSS_v3_PR_L" type="radio" /><label
                        for="CVSS_v3_PR_L" id="CVSS_v3_PR_L_Label"
                        title="The attacker is authorized with (i.e., requires) privileges that provide basic user capabilities that could normally affect only settings and files owned by a user. Alternatively, an attacker with Low privileges may have the ability to cause an impact only to non-sensitive resources.">Low
                        (L)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="PR" name="CVSS_V3_PR" value="H" id="CVSS_v3_PR_H" type="radio" /><label
                        for="CVSS_v3_PR_H" id="CVSS_v3_PR_H_Label"
                        title="The attacker is authorized with (i.e., requires) privileges that provide significant (e.g., administrative) control over the vulnerable component that could affect component-wide settings and files.">High
                        (H)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_UI_Heading"
                        title="This metric captures the requirement for a user, other than the attacker, to participate in the successful compromise the vulnerable component. This metric determines whether the vulnerability can be exploited solely at the will of the attacker, or whether a separate user (or user-initiated process) must participate in some manner.">
                        User Interaction (UI)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="UI" name="CVSS_V3_UI" value="N" id="CVSS_v3_UI_N" type="radio" /><label
                        for="CVSS_v3_UI_N" id="CVSS_v3_UI_N_Label"
                        title="The vulnerable system can be exploited without any interaction from any user.">None
                        (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="UI" name="CVSS_V3_UI" value="R" id="CVSS_v3_UI_R" type="radio" /><label
                        for="CVSS_v3_UI_R" id="CVSS_v3_UI_R_Label"
                        title="Successful exploitation of this vulnerability requires a user to take some action before the vulnerability can be exploited.">Required
                        (R)</label>
                </div>

            </div>


            <div class="col-lg-6">

                <div class="cvss-metric">
                    <div id="CVSS_v3_S_Heading"
                        title="Does a successful attack impact a component other than the vulnerable component? If so, the Base Score increases and the Confidentiality, Integrity and Authentication metrics should be scored relative to the impacted component.">
                        Scope (S)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="S" name="CVSS_V3_S" value="U" id="CVSS_v3_S_U" type="radio" /><label for="CVSS_v3_S_U"
                        id="CVSS_v3_S_U_Label"
                        title="An exploited vulnerability can only affect resources managed by the same security authority. In this case, the vulnerable component and the impacted component are either the same, or both are managed by the same security authority.">Unchanged
                        (U)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="S" name="CVSS_V3_S" value="C" id="CVSS_v3_S_C" type="radio" /><label for="CVSS_v3_S_C"
                        id="CVSS_v3_S_C_Label"
                        title="An exploited vulnerability can affect resources beyond the security scope managed by the security authority of the vulnerable component. In this case, the vulnerable component and the impacted component are different and managed by different security authorities.">Changed
                        (C)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_C_Heading"
                        title="This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability. Confidentiality refers to limiting information access and disclosure to only authorized users, as well as preventing access by, or disclosure to, unauthorized ones.">
                        Confidentiality (C)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="C" name="CVSS_V3_C" value="N" id="CVSS_v3_C_N" type="radio" /><label for="CVSS_v3_C_N"
                        id="CVSS_v3_C_N_Label" title="There is no loss of confidentiality within the impacted component.">None
                        (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="C" name="CVSS_V3_C" value="L" id="CVSS_v3_C_L" type="radio" /><label for="CVSS_v3_C_L"
                        id="CVSS_v3_C_L_Label"
                        title="There is some loss of confidentiality. Access to some restricted information is obtained, but the attacker does not have control over what information is obtained, or the amount or kind of loss is limited. The information disclosure does not cause a direct, serious loss to the impacted component.">Low
                        (L)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="C" name="CVSS_V3_C" value="H" id="CVSS_v3_C_H" type="radio" /><label for="CVSS_v3_C_H"
                        id="CVSS_v3_C_H_Label"
                        title="There is total loss of confidentiality, resulting in all resources within the impacted component being divulged to the attacker. Alternatively, access to only some restricted information is obtained, but the disclosed information presents a direct, serious impact.">High
                        (H)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_I_Heading"
                        title="This metric measures the impact to integrity of a successfully exploited vulnerability. Integrity refers to the trustworthiness and veracity of information.">
                        Integrity (I)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="I" name="CVSS_V3_I" value="N" id="CVSS_v3_I_N" type="radio" /><label for="CVSS_v3_I_N"
                        id="CVSS_v3_I_N_Label" title="There is no loss of integrity within the impacted component.">None (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="I" name="CVSS_V3_I" value="L" id="CVSS_v3_I_L" type="radio" /><label for="CVSS_v3_I_L"
                        id="CVSS_v3_I_L_Label"
                        title="Modification of data is possible, but the attacker does not have control over the consequence of a modification, or the amount of modification is limited. The data modification does not have a direct, serious impact on the impacted component.">Low
                        (L)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="I" name="CVSS_V3_I" value="H" id="CVSS_v3_I_H" type="radio" /><label for="CVSS_v3_I_H"
                        id="CVSS_v3_I_H_Label"
                        title="There is a total loss of integrity, or a complete loss of protection. For example, the attacker is able to modify any/all files protected by the impacted component. Alternatively, only some files can be modified, but malicious modification would present a direct, serious consequence to the impacted component.">High
                        (H)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_A_Heading"
                        title="This metric measures the impact to the availability of the impacted component resulting from a successfully exploited vulnerability. It refers to the loss of availability of the impacted component itself, such as a networked service (e.g., web, database, email). Since availability refers to the accessibility of information resources, attacks that consume network bandwidth, processor cycles, or disk space all impact the availability of an impacted component.">
                        Availability (A)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="A" name="CVSS_V3_A" value="N" id="CVSS_v3_A_N" type="radio" /><label for="CVSS_v3_A_N"
                        id="CVSS_v3_A_N_Label" title="There is no impact to availability within the impacted component.">None
                        (N)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="A" name="CVSS_V3_A" value="L" id="CVSS_v3_A_L" type="radio" /><label for="CVSS_v3_A_L"
                        id="CVSS_v3_A_L_Label"
                        title="Performance is reduced or there are interruptions in resource availability. Even if repeated exploitation of the vulnerability is possible, the attacker does not have the ability to completely deny service to legitimate users. The resources in the impacted component are either partially available all of the time, or fully available only some of the time, but overall there is no direct, serious consequence to the impacted component.">Low
                        (L)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="A" name="CVSS_V3_A" value="H" id="CVSS_v3_A_H" type="radio" /><label for="CVSS_v3_A_H"
                        id="CVSS_v3_A_H_Label"
                        title="There is total loss of availability, resulting in the attacker being able to fully deny access to resources in the impacted component; this loss is either sustained (while the attacker continues to deliver the attack) or persistent (the condition persists even after the attack has completed). Alternatively, the attacker has the ability to deny some availability, but the loss of availability presents a direct, serious consequence to the impacted component (e.g., the attacker cannot disrupt existing connections, but can prevent new connections; the attacker can repeatedly exploit a vulnerability that, in each instance of a successful attack, leaks a only small amount of memory, but after repeated exploitation causes a service to become completely unavailable).">High
                        (H)</label>
                </div>

            </div>

        </fieldset>

        <fieldset id="CVSS_v3_temporalMetricGroup" class="cvss-fieldset">
            <legend id="CVSS_v3_temporalMetricGroup_Legend"
                title="The Temporal metrics measure the current state of exploit techniques or code availability, the existence of any patches or workarounds, or the confidence that one has in the description of a vulnerability.">
                Temporal Score</legend>

            <div class="col-lg-6">

                <div class="cvss-metric">
                    <div id="CVSS_v3_E_Heading"
                        title="This metric measures the likelihood of the vulnerability being attacked, and is typically based on the current state of exploit techniques, exploit code availability, or active, 'in-the-wild' exploitation.">
                        Exploit Code Maturity (E)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V3_E" value="X" id="CVSS_v3_E_X" checked="" type="radio" /><label
                        for="CVSS_v3_E_X" id="CVSS_v3_E_X_Label"
                        title="Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Temporal Score, i.e., it has the same effect on scoring as assigning High.">Not
                        Defined (X)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V3_E" value="U" id="CVSS_v3_E_U" type="radio" /><label for="CVSS_v3_E_U"
                        id="CVSS_v3_E_U_Label" title="No exploit code is available, or an exploit is theoretical.">Unproven
                        (U)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V3_E" value="P" id="CVSS_v3_E_P" type="radio" /><label for="CVSS_v3_E_P"
                        id="CVSS_v3_E_P_Label"
                        title="Proof-of-concept exploit code is available, or an attack demonstration is not practical for most systems. The code or technique is not functional in all situations and may require substantial modification by a skilled attacker.">Proof-of-Concept
                        (P)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V3_E" value="F" id="CVSS_v3_E_F" type="radio" /><label for="CVSS_v3_E_F"
                        id="CVSS_v3_E_F_Label"
                        title="Functional exploit code is available. The code works in most situations where the vulnerability exists.">Functional
                        (F)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="E" name="CVSS_V3_E" value="H" id="CVSS_v3_E_H" type="radio" /><label for="CVSS_v3_E_H"
                        id="CVSS_v3_E_H_Label"
                        title="Functional autonomous code exists, or no exploit is required (manual trigger) and details are widely available. Exploit code works in every situation, or is actively being delivered via an autonomous agent (such as a worm or virus). Network-connected systems are likely to encounter scanning or exploitation attempts. Exploit development has reached the level of reliable, widely-available, easy-to-use automated tools.">High
                        (H)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_RL_Heading"
                        title="The Remediation Level of a vulnerability is an important factor for prioritization. The typical vulnerability is unpatched when initially published. Workarounds or hotfixes may offer interim remediation until an official patch or upgrade is issued. Each of these respective stages adjusts the temporal score downwards, reflecting the decreasing urgency as remediation becomes final.">
                        Remediation Level (RL)</div>
                    <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V3_RL" value="X" id="CVSS_v3_RL_X" checked=""
                        type="radio" /><label for="CVSS_v3_RL_X" id="CVSS_v3_RL_X_Label"
                        title="Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Temporal Score, i.e., it has the same effect on scoring as assigning Unavailable.">Not
                        Defined (X)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V3_RL" value="O" id="CVSS_v3_RL_O" type="radio" /><label
                        for="CVSS_v3_RL_O" id="CVSS_v3_RL_O_Label"
                        title="A complete vendor solution is available. Either the vendor has issued an official patch, or an upgrade is available.">Official
                        Fix (O)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V3_RL" value="T" id="CVSS_v3_RL_T" type="radio" /><label
                        for="CVSS_v3_RL_T" id="CVSS_v3_RL_T_Label"
                        title="There is an official but temporary fix available. This includes instances where the vendor issues a temporary hotfix, tool, or workaround.">Temporary
                        Fix (T)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V3_RL" value="W" id="CVSS_v3_RL_W" type="radio" /><label
                        for="CVSS_v3_RL_W" id="CVSS_v3_RL_W_Label"
                        title="There is an unofficial, non-vendor solution available. In some cases, users of the affected technology will create a patch of their own or provide steps to work around or otherwise mitigate the vulnerability.">Workaround
                        (W)</label>
                    <input :disabled="readOnly" @change="calculateScore" v-model="RL" name="CVSS_V3_RL" value="U" id="CVSS_v3_RL_U" type="radio" /><label
                        for="CVSS_v3_RL_U" id="CVSS_v3_RL_U_Label"
                        title="There is either no solution available or it is impossible to apply.">Unavailable (U)</label>
                </div>

                <div class="cvss-metric">
                    <div id="CVSS_v3_RC_Heading"
                    title="This metric measures the degree of confidence in the existence of the vulnerability and the credibility of the known technical details. Sometimes only the existence of vulnerabilities are publicized, but without specific details. For example, an impact may be recognized as undesirable, but the root cause may not be known. The vulnerability may later be corroborated by research which suggests where the vulnerability may lie, though the research may not be certain. Finally, a vulnerability may be confirmed through acknowledgement by the author or vendor of the affected technology. The urgency of a vulnerability is higher when a vulnerability is known to exist with certainty. This metric also suggests the level of technical knowledge available to would-be attackers.">
                    Report Confidence (RC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V3_RC" value="X" id="CVSS_v3_RC_X" checked=""
                    type="radio" /><label for="CVSS_v3_RC_X" id="CVSS_v3_RC_X_Label"
                    title="Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Temporal Score, i.e., it has the same effect on scoring as assigning Confirmed.">Not
                    Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V3_RC" value="U" id="CVSS_v3_RC_U" type="radio" /><label
                    for="CVSS_v3_RC_U" id="CVSS_v3_RC_U_Label"
                    title="There are reports of impacts that indicate a vulnerability is present. The reports indicate that the cause of the vulnerability is unknown, or reports may differ on the cause or impacts of the vulnerability. Reporters are uncertain of the true nature of the vulnerability, and there is little confidence in the validity of the reports or whether a static Base score can be applied given the differences described. An example is a bug report which notes that an intermittent but non-reproducible crash occurs, with evidence of memory corruption suggesting that denial of service, or possible more serious impacts, may result.">Unknown
                    (U)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V3_RC" value="R" id="CVSS_v3_RC_R" type="radio" /><label
                    for="CVSS_v3_RC_R" id="CVSS_v3_RC_R_Label"
                    title="Significant details are published, but researchers either do not have full confidence in the root cause, or do not have access to source code to fully confirm all of the interactions that may lead to the result. Reasonable confidence exists, however, that the bug is reproducible and at least one impact is able to be verified (Proof-of-concept exploits may provide this). An example is a detailed write-up of research into a vulnerability with an explanation (possibly obfuscated or 'left as an exercise to the reader') that gives assurances on how to reproduce the results.">Reasonable
                    (R)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="RC" name="CVSS_V3_RC" value="C" id="CVSS_v3_RC_C" type="radio" /><label
                    for="CVSS_v3_RC_C" id="CVSS_v3_RC_C_Label"
                    title="Detailed reports exist, or functional reproduction is possible (functional exploits may provide this). Source code is available to independently verify the assertions of the research, or the author or vendor of the affected code has confirmed the presence of the vulnerability.">Confirmed
                    (C)</label>
            </div>

        </div>


    </fieldset>

    <fieldset id="CVSS_v3_environmentalMetricGroup" class="cvss-fieldset">
        <legend id="CVSS_v3_environmentalMetricGroup_Legend"
            title="These metrics enable the analyst to customize the CVSS score depending on the importance of the affected IT asset to a user’s organization, measured in terms of complementary/alternative security controls in place, Confidentiality, Integrity, and Availability. The metrics are the modified equivalent of base metrics and are assigned metric values based on the component placement in organization infrastructure.">
            Environmental Score</legend>

        <div class="col-lg-6">

            <div class="cvss-metric">
                <div id="CVSS_v3_CR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the Confidentiality of the affected IT asset to a user’s organization, relative to other impacts. This metric modifies the environmental score by reweighting the Modified Confidentiality impact metric versus the other modified impacts.">
                    Confidentiality Requirement (CR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V3_CR" value="X" id="CVSS_v3_CR_X" checked=""
                    type="radio" /><label for="CVSS_v3_CR_X" id="CVSS_v3_CR_X_Label"
                    title="Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Environmental Score, i.e., it has the same effect on scoring as assigning Medium.">Not
                    Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V3_CR" value="L" id="CVSS_v3_CR_L" type="radio" /><label
                    for="CVSS_v3_CR_L" id="CVSS_v3_CR_L_Label"
                    title="Loss of Confidentiality is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V3_CR" value="M" id="CVSS_v3_CR_M" type="radio" /><label
                    for="CVSS_v3_CR_M" id="CVSS_v3_CR_M_Label"
                    title="Assigning this value to the metric will not influence the score.">Medium (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="CR" name="CVSS_V3_CR" value="H" id="CVSS_v3_CR_H" type="radio" /><label
                    for="CVSS_v3_CR_H" id="CVSS_v3_CR_H_Label"
                    title="Loss of Confidentiality is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_IR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the Integrity of the affected IT asset to a user’s organization, relative to other impacts. This metric modifies the environmental score by reweighting the Modified Integrity impact metric versus the other modified impacts.">
                    Integrity Requirement (IR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V3_IR" value="X" id="CVSS_v3_IR_X" checked=""
                    type="radio" /><label for="CVSS_v3_IR_X" id="CVSS_v3_IR_X_Label"
                    title="Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Environmental Score, i.e., it has the same effect on scoring as assigning Medium.">Not
                    Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V3_IR" value="L" id="CVSS_v3_IR_L" type="radio" /><label
                    for="CVSS_v3_IR_L" id="CVSS_v3_IR_L_Label"
                    title="Loss of Integrity is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V3_IR" value="M" id="CVSS_v3_IR_M" type="radio" /><label
                    for="CVSS_v3_IR_M" id="CVSS_v3_IR_M_Label"
                    title="Assigning this value to the metric will not influence the score.">Medium (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="IR" name="CVSS_V3_IR" value="H" id="CVSS_v3_IR_H" type="radio" /><label
                    for="CVSS_v3_IR_H" id="CVSS_v3_IR_H_Label"
                    title="Loss of Integrity is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High
                    (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_AR_Heading"
                    title="These metrics enable the analyst to customize the CVSS score depending on the importance of the Availability of the affected IT asset to a user’s organization, relative to other impacts. This metric modifies the environmental score by reweighting the Modified Availability impact metric versus the other modified impacts.">
                    Availability Requirement (AR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V3_AR" value="X" id="CVSS_v3_AR_X" checked=""
                    type="radio" /><label for="CVSS_v3_AR_X" id="CVSS_v3_AR_X_Label"
                    title="Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Environmental Score, i.e., it has the same effect on scoring as assigning Medium.">Not
                    Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V3_AR" value="L" id="CVSS_v3_AR_L" type="radio" /><label
                    for="CVSS_v3_AR_L" id="CVSS_v3_AR_L_Label"
                    title="Loss of Availability is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">Low
                    (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V3_AR" value="M" id="CVSS_v3_AR_M" type="radio" /><label
                    for="CVSS_v3_AR_M" id="CVSS_v3_AR_M_Label"
                    title="Assigning this value to the metric will not influence the score.">Medium (M)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="AR" name="CVSS_V3_AR" value="H" id="CVSS_v3_AR_H" type="radio" /><label
                    for="CVSS_v3_AR_H" id="CVSS_v3_AR_H_Label"
                    title="Loss of Availability is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).">High
                    (H)</label>
            </div>
        </div>

        <div class="col-lg-6">
            <div class="cvss-metric">
                <div id="CVSS_v3_MAV_Heading"
                    title="This metric reflects the context by which vulnerability exploitation is possible. The Environmental Score increases the more remote (logically, and physically) an attacker can be in order to exploit the vulnerable component.">
                    Modified Attack Vector (MAV)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V3_MAV" value="X" id="CVSS_v3_MAV_X" checked=""
                    type="radio" /><label for="CVSS_v3_MAV_X" id="CVSS_v3_MAV_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V3_MAV" value="N" id="CVSS_v3_MAV_N" type="radio" /><label
                    for="CVSS_v3_MAV_N" id="CVSS_v3_MAV_N_Label"
                    title="The vulnerable component is bound to the network stack and the set of possible attackers extends beyond the other options listed, up to and including the entire Internet. Such a vulnerability is often termed 'remotely exploitable' and can be thought of as an attack being exploitable at the protocol level one or more network hops away.">Network (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V3_MAV" value="A" id="CVSS_v3_MAV_A" type="radio" /><label
                    for="CVSS_v3_MAV_A" id="CVSS_v3_MAV_A_Label"
                    title="The vulnerable component is bound to the network stack, but the attack is limited at the protocol level to a logically adjacent topology. This can mean an attack must be launched from the same shared physical (e.g., Bluetooth or IEEE 802.11) or logical (e.g., local IP subnet) network, or from within a secure or otherwise limited administrative domain (e.g., MPLS, secure VPN).">Adjacent
                    Network</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V3_MAV" value="L" id="CVSS_v3_MAV_L" type="radio" /><label
                    for="CVSS_v3_MAV_L" id="CVSS_v3_MAV_L_Label"
                    title="The vulnerable component is not bound to the network stack and the attacker’s path is via read/write/execute capabilities. Either: the attacker exploits the vulnerability by accessing the target system locally (e.g., keyboard, console), or remotely (e.g., SSH); or the attacker relies on User Interaction by another person to perform actions required to exploit the vulnerability (e.g., tricking a legitimate user into opening a malicious document).">Local</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAV" name="CVSS_V3_MAV" value="P" id="CVSS_v3_MAV_P" type="radio" /><label
                    for="CVSS_v3_MAV_P" id="CVSS_v3_MAV_P_Label"
                    title="The attack requires the attacker to physically touch or manipulate the vulnerable component. Physical interaction may be brief or persistent.">Physical</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_MAC_Heading"
                    title="This metric describes the conditions beyond the attacker’s control that must exist in order to exploit the vulnerability. Such conditions may require the collection of more information about the target or computational exceptions. The assessment of this metric excludes any requirements for user interaction in order to exploit the vulnerability. If a specific configuration is required for an attack to succeed, the Base metrics should be scored assuming the vulnerable component is in that configuration.">
                    Modified Attack Complexity (MAC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAC" name="CVSS_V3_MAC" value="X" id="CVSS_v3_MAC_X" checked=""
                    type="radio" /><label for="CVSS_v3_MAC_X" id="CVSS_v3_MAC_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAC" name="CVSS_V3_MAC" value="L" id="CVSS_v3_MAC_L" type="radio" /><label
                    for="CVSS_v3_MAC_L" id="CVSS_v3_MAC_L_Label"
                    title="Specialized access conditions or extenuating circumstances do not exist. An attacker can expect repeatable success against the vulnerable component.">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MAC" name="CVSS_V3_MAC" value="H" id="CVSS_v3_MAC_H" type="radio" /><label
                    for="CVSS_v3_MAC_H" id="CVSS_v3_MAC_H_Label"
                    title="A successful attack depends on conditions beyond the attacker's control. That is, a successful attack cannot be accomplished at will, but requires the attacker to invest in some measurable amount of effort in preparation or execution against the vulnerable component before a successful attack can be expected. For example, a successful attack may require an attacker to: gather knowledge about the environment in which the vulnerable target/component exists; prepare the target environment to improve exploit reliability; or inject themselves into the logical network path between the target and the resource requested by the victim in order to read and/or modify network communications (e.g., a man in the middle attack).">High (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_MPR_Heading"
                    title="This metric describes the level of privileges an attacker must possess before successfully exploiting the vulnerability.">
                    Modified Privileges Required (MPR)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V3_MPR" value="X" id="CVSS_v3_MPR_X" checked=""
                    type="radio" /><label for="CVSS_v3_MPR_X" id="CVSS_v3_MPR_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V3_MPR" value="N" id="CVSS_v3_MPR_N" type="radio" /><label
                    for="CVSS_v3_MPR_N" id="CVSS_v3_MPR_N_Label"
                    title="The attacker is unauthorized prior to attack, and therefore does not require any access to settings or files to carry out an attack.">None (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V3_MPR" value="L" id="CVSS_v3_MPR_L" type="radio" /><label
                    for="CVSS_v3_MPR_L" id="CVSS_v3_MPR_L_Label"
                    title="The attacker is authorized with (i.e., requires) privileges that provide basic user capabilities that could normally affect only settings and files owned by a user. Alternatively, an attacker with Low privileges may have the ability to cause an impact only to non-sensitive resources.">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MPR" name="CVSS_V3_MPR" value="H" id="CVSS_v3_MPR_H" type="radio" /><label
                    for="CVSS_v3_MPR_H" id="CVSS_v3_MPR_H_Label"
                    title="The attacker is authorized with (i.e., requires) privileges that provide significant (e.g., administrative) control over the vulnerable component that could affect component-wide settings and files.">High (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_MUI_Heading"
                    title="This metric captures the requirement for a user, other than the attacker, to participate in the successful compromise the vulnerable component. This metric determines whether the vulnerability can be exploited solely at the will of the attacker, or whether a separate user (or user-initiated process) must participate in some manner.">
                    Modified User Interaction (MUI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MUI" name="CVSS_V3_MUI" value="X" id="CVSS_v3_MUI_X" checked=""
                    type="radio" /><label for="CVSS_v3_MUI_X" id="CVSS_v3_MUI_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MUI" name="CVSS_V3_MUI" value="N" id="CVSS_v3_MUI_N" type="radio" /><label
                    for="CVSS_v3_MUI_N" id="CVSS_v3_MUI_N_Label"
                    title="The vulnerable system can be exploited without any interaction from any user.">None (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MUI" name="CVSS_V3_MUI" value="R" id="CVSS_v3_MUI_R" type="radio" /><label
                    for="CVSS_v3_MUI_R" id="CVSS_v3_MUI_R_Label"
                    title="Successful exploitation of this vulnerability requires a user to take some action before the vulnerability can be exploited.">Required (R)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_MS_Heading"
                    title="Does a successful attack impact a component other than the vulnerable component? If so, the Base Score increases and the Confidentiality, Integrity and Authentication metrics should be scored relative to the impacted component.">
                    Modified Scope (MS)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MS" name="CVSS_V3_MS" value="X" id="CVSS_v3_MS_X" checked=""
                    type="radio" /><label for="CVSS_v3_MS_X" id="CVSS_v3_MS_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MS" name="CVSS_V3_MS" value="U" id="CVSS_v3_MS_U" type="radio" /><label
                    for="CVSS_v3_MS_U" id="CVSS_v3_MS_U_Label"
                    title="An exploited vulnerability can only affect resources managed by the same security authority. In this case, the vulnerable component and the impacted component are either the same, or both are managed by the same security authority.">Unchanged (U)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MS" name="CVSS_V3_MS" value="C" id="CVSS_v3_MS_C" type="radio" /><label
                    for="CVSS_v3_MS_C" id="CVSS_v3_MS_C_Label"
                    title="An exploited vulnerability can affect resources beyond the security scope managed by the security authority of the vulnerable component. In this case, the vulnerable component and the impacted component are different and managed by different security authorities.">Changed (C)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_MC_Heading"
                    title="This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability. Confidentiality refers to limiting information access and disclosure to only authorized users, as well as preventing access by, or disclosure to, unauthorized ones.">
                    Modified Confidentiality (MC)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MC" name="CVSS_V3_MC" value="X" id="CVSS_v3_MC_X" checked=""
                    type="radio" /><label for="CVSS_v3_MC_X" id="CVSS_v3_MC_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MC" name="CVSS_V3_MC" value="N" id="CVSS_v3_MC_N" type="radio" /><label
                    for="CVSS_v3_MC_N" id="CVSS_v3_MC_N_Label"
                    title="There is no loss of confidentiality within the impacted component.">None (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MC" name="CVSS_V3_MC" value="L" id="CVSS_v3_MC_L" type="radio" /><label
                    for="CVSS_v3_MC_L" id="CVSS_v3_MC_L_Label"
                    title="There is some loss of confidentiality. Access to some restricted information is obtained, but the attacker does not have control over what information is obtained, or the amount or kind of loss is limited. The information disclosure does not cause a direct, serious loss to the impacted component.">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MC" name="CVSS_V3_MC" value="H" id="CVSS_v3_MC_H" type="radio" /><label
                    for="CVSS_v3_MC_H" id="CVSS_v3_MC_H_Label"
                    title="There is total loss of confidentiality, resulting in all resources within the impacted component being divulged to the attacker. Alternatively, access to only some restricted information is obtained, but the disclosed information presents a direct, serious impact.">High (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_MI_Heading"
                    title="This metric measures the impact to integrity of a successfully exploited vulnerability. Integrity refers to the trustworthiness and veracity of information.">
                    Modified Integrity (MI)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MI" name="CVSS_V3_MI" value="X" id="CVSS_v3_MI_X" checked=""
                    type="radio" /><label for="CVSS_v3_MI_X" id="CVSS_v3_MI_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MI" name="CVSS_V3_MI" value="N" id="CVSS_v3_MI_N" type="radio" /><label
                    for="CVSS_v3_MI_N" id="CVSS_v3_MI_N_Label"
                    title="There is no loss of integrity within the impacted component.">None (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MI" name="CVSS_V3_MI" value="L" id="CVSS_v3_MI_L" type="radio" /><label
                    for="CVSS_v3_MI_L" id="CVSS_v3_MI_L_Label"
                    title="Modification of data is possible, but the attacker does not have control over the consequence of a modification, or the amount of modification is limited. The data modification does not have a direct, serious impact on the impacted component.">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MI" name="CVSS_V3_MI" value="H" id="CVSS_v3_MI_H" type="radio" /><label
                    for="CVSS_v3_MI_H" id="CVSS_v3_MI_H_Label"
                    title="There is a total loss of integrity, or a complete loss of protection. For example, the attacker is able to modify any/all files protected by the impacted component. Alternatively, only some files can be modified, but malicious modification would present a direct, serious consequence to the impacted component.">High (H)</label>
            </div>

            <div class="cvss-metric">
                <div id="CVSS_v3_MA_Heading"
                    title="This metric measures the impact to the availability of the impacted component resulting from a successfully exploited vulnerability. It refers to the loss of availability of the impacted component itself, such as a networked service (e.g., web, database, email). Since availability refers to the accessibility of information resources, attacks that consume network bandwidth, processor cycles, or disk space all impact the availability of an impacted component.">
                    Modified Availability (MA)</div>
                <input :disabled="readOnly" @change="calculateScore" v-model="MA" name="CVSS_V3_MA" value="X" id="CVSS_v3_MA_X" checked=""
                    type="radio" /><label for="CVSS_v3_MA_X" id="CVSS_v3_MA_X_Label"
                    title="The value assigned to the corresponding Base metric is used.">Not Defined (X)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MA" name="CVSS_V3_MA" value="N" id="CVSS_v3_MA_N" type="radio" /><label
                    for="CVSS_v3_MA_N" id="CVSS_v3_MA_N_Label"
                    title="There is no impact to availability within the impacted component.">None (N)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MA" name="CVSS_V3_MA" value="L" id="CVSS_v3_MA_L" type="radio" /><label
                    for="CVSS_v3_MA_L" id="CVSS_v3_MA_L_Label"
                    title="Performance is reduced or there are interruptions in resource availability. Even if repeated exploitation of the vulnerability is possible, the attacker does not have the ability to completely deny service to legitimate users. The resources in the impacted component are either partially available all of the time, or fully available only some of the time, but overall there is no direct, serious consequence to the impacted component.">Low (L)</label>
                <input :disabled="readOnly" @change="calculateScore" v-model="MA" name="CVSS_V3_MA" value="H" id="CVSS_v3_MA_H" type="radio" /><label
                    for="CVSS_v3_MA_H" id="CVSS_v3_MA_H_Label"
                    title="There is total loss of availability, resulting in the attacker being able to fully deny access to resources in the impacted component; this loss is either sustained (while the attacker continues to deliver the attack) or persistent (the condition persists even after the attack has completed). Alternatively, the attacker has the ability to deny some availability, but the loss of availability presents a direct, serious consequence to the impacted component (e.g., the attacker cannot disrupt existing connections, but can prevent new connections; the attacker can repeatedly exploit a vulnerability that, in each instance of a successful attack, leaks a only small amount of memory, but after repeated exploitation causes a service to become completely unavailable).">High (H)</label>
            </div>
        </div>

    </fieldset>
</div>
`,
};
